import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { deleteItems, updateStateData } from "../../../Store/actions";
import MultiSelect from "../Selectors/MultiSelect";
import GroupedMultiSelect from "../Selectors/GroupedMultiSelect";
import { DetailedSelectionIconWithTooltip } from "../../../api_calls/utils";
import StoreDetailTable from "../DetailedStore/StoreSelectionModal";
import { loadRetailerAccessAvailability } from "../../../api_calls/retailerSelection";
import { checkUserRole } from "../../../api_calls/userManagement";
import {
  ROOT_RETAILER_GROUP_PARAM,
  resolveLoggedInUserType,
  rootRetailerOptionFields,
  shouldUseRetailerStoreHierarchy,
} from "./storeFilterUtils";

/**
 * Brand Report popup store picker.
 * Uses analytics_campaign_stores_list (same source as the live analytics dashboard).
 */
const BrandStoresFilter = ({ usedFor }) => {
  const storeDetails = useSelector((state) => state.userSelections);
  const [showSelectionModal, setShowSelectionModal] = React.useState(false);
  const [retailerHierarchyAvailable, setRetailerHierarchyAvailable] = React.useState(false);
  const dispatchAction = useDispatch();
  const loggedInUserType = resolveLoggedInUserType();
  const isBrandUser =
    storeDetails?.brand_report_view === true || checkUserRole()?.user_role === "Brand";

  const detailedStoresListForCampaign =
    typeof storeDetails.analytics_campaign_stores_list !== "undefined" &&
    storeDetails.analytics_campaign_stores_list.length !== 0
      ? storeDetails.analytics_campaign_stores_list
      : [];

  const storeList =
    detailedStoresListForCampaign.length !== 0
      ? detailedStoresListForCampaign.map((store) => store.name).sort()
      : [];

  // Keep one option per listed name, including duplicates. Root retailer is only for grouping.
  const storeOptions = storeList.map((storeName) => {
    const storeRecord = detailedStoresListForCampaign.find((store) => store.name === storeName);
    return {
      value: storeName,
      label: storeName,
      ...rootRetailerOptionFields(storeRecord),
    };
  });
  const showRetailerHierarchy = shouldUseRetailerStoreHierarchy({
    isBrand: isBrandUser,
    retailerHierarchyAvailable,
    storeOptions,
  });

  React.useEffect(() => {
    let shouldApplyResult = true;

    if (loggedInUserType === "") {
      setRetailerHierarchyAvailable(false);
      return undefined;
    }

    loadRetailerAccessAvailability(loggedInUserType)
      .then((retailerAccessInfo) => {
        if (shouldApplyResult) {
          setRetailerHierarchyAvailable(retailerAccessInfo.available === true);
        }
      })
      .catch(() => {
        if (shouldApplyResult) {
          setRetailerHierarchyAvailable(false);
        }
      });

    return () => {
      shouldApplyResult = false;
    };
  }, [loggedInUserType]);

  const selectedStoreOptions =
    typeof storeDetails.brand_filters !== "undefined" &&
    typeof storeDetails.brand_filters[usedFor] !== "undefined" &&
    typeof storeDetails.brand_filters[usedFor].stores !== "undefined"
      ? storeDetails.brand_filters[usedFor].stores.map((storeName) => {
          return { value: storeName, label: storeName };
        })
      : [];

  // Saved value stays a list of store names.
  const saveSelectedStoreNames = (selectedOption) => {
    let userChoices = [];
    const preselectedBrandFilters =
      typeof storeDetails.brand_filters !== "undefined"
        ? storeDetails.brand_filters
        : {};
    selectedOption.forEach((chosen) => {
      if (chosen.label !== "(All)") {
        userChoices.push(chosen.label);
      } else {
        userChoices.push([...new Set(storeList)]);
      }
    });
    dispatchAction(
      updateStateData({
        brand_filters: {
          ...preselectedBrandFilters,
          [usedFor]: {
            ...preselectedBrandFilters[usedFor],
            stores: userChoices,
          },
        },
        report_filter_saved: false,
      })
    );
  };

  const detailIcon = (setOpen) => {
    return DetailedSelectionIconWithTooltip((e) => {
      e.stopPropagation();
      setOpen(false);
      setShowSelectionModal(true);
    });
  };

  const storeSelect = showRetailerHierarchy === true ? (
    <GroupedMultiSelect
      enableFullWidth={true}
      elemId={usedFor + "_select"}
      label={"Store"}
      options={storeOptions}
      defaultVal={selectedStoreOptions}
      groupParam={ROOT_RETAILER_GROUP_PARAM}
      showGroupNames={true}
      showSelectionSummary={true}
      selectionCheck={(option, value) => option.label === value.label}
      usedFor="brandStore"
      onChangeCall={saveSelectedStoreNames}
      additionalIconWithTooltip={true}
      additionalIconMarkup={detailIcon}
    />
  ) : (
    <MultiSelect
      enableFullWidth={true}
      elemId={usedFor + "_select"}
      label={"Store"}
      options={storeOptions}
      defaultVal={selectedStoreOptions}
      selectionCheck={(option, value) => option.label === value.label}
      usedFor="brandStore"
      onChangeCall={saveSelectedStoreNames}
      additionalIconWithTooltip={true}
      additionalIconMarkup={detailIcon}
      allowDuplicates={true}
    />
  );

  return (
    <>
      {storeSelect}

      {showSelectionModal && (
        <StoreDetailTable
          open={showSelectionModal}
          onClose={() => {
            setShowSelectionModal(false);
            dispatchAction(
              deleteItems([
                "store_selection_pagination",
                "store_list_filter_applied",
                "detailed_stores_filtered",
              ])
            );
          }}
          applyChangesOnSelect={false}
          selectedValues={
            typeof storeDetails.brand_filters !== "undefined" &&
            typeof storeDetails.brand_filters[usedFor] !== "undefined" &&
            typeof storeDetails.brand_filters[usedFor].stores !== "undefined"
              ? detailedStoresListForCampaign
                  .filter((storeDetail) =>
                    storeDetails.brand_filters[usedFor].stores.includes(
                      storeDetail.name
                    )
                  )
                  .map((storeDtl) => storeDtl.id)
              : []
          }
          toHandleApplyChanges={(selectedStoreIds, selectedStoreNames) => {
            const preselectedBrandFilters =
              typeof storeDetails.brand_filters !== "undefined"
                ? storeDetails.brand_filters
                : {};

            dispatchAction(
              updateStateData({
                brand_filters: {
                  ...preselectedBrandFilters,
                  [usedFor]: {
                    ...preselectedBrandFilters[usedFor],
                    stores: selectedStoreNames.map(
                      (selectedStore) => selectedStore.label
                    ),
                  },
                },
                report_filter_saved: false,
              })
            );
          }}
          storeOptionsList={detailedStoresListForCampaign}
        />
      )}
    </>
  );
};

export default BrandStoresFilter;
